// Global Instructions Rule Applied!
/**
 * Helpers for `user_preferences_reports` rows loaded with global configs.
 *
 * Semantics (DB/API):
 * - One row per user per `menu_item_id` (upsert conflict on user_id + menu_item_id).
 * - `report_id` is a legacy column name: it stores a single scalar id when the preference
 *   is “pick one entity” (e.g. selected fund on Fundraising Pipeline). Prefer `data` for
 *   structured or multi-field preferences.
 * - Use {@link getMenuItemIdByKey} with `menu_items` from global configs and a stable key
 *   from {@link ../../state_manager/globals.ts} (e.g. MENU_ITEM_KEY_*).
 */

import type { MenuItemModel } from '../../state_manager/data_models/menu_item_model.ts'
import type { UserPreferenceReportModel } from '../../state_manager/data_models/user_preference_report_model.ts'
import { deleteUserPreference, persistUserPreference } from './controller.ts'

/** Minimal menu item shape for lookups (matches global config `menu_items`). */
export type MenuItemKeyRow = Pick<MenuItemModel, 'id' | 'key'>

/**
 * Resolve Supabase `menu_items.id` for a given `menu_items.key` (e.g. fundraising-pipeline).
 *
 * A key can sit in several menu categories (Operations repeats Funds, Prospects
 * and Consolidated Funds), and `menu_items` arrives ordered by `sequence` alone,
 * so "the first item with the key" depends on where each copy sits in its own
 * category. A caller whose preferences must stay on one particular copy passes
 * its id as `preferredMenuItemId`; it wins whenever it carries the key.
 */
export function getMenuItemIdByKey(
  menuItems: ReadonlyArray<MenuItemKeyRow>,
  menuItemKey: string,
  preferredMenuItemId?: string | null
): string | undefined {
  if (preferredMenuItemId && menuItems.some((m) => m.key === menuItemKey && m.id === preferredMenuItemId)) {
    return preferredMenuItemId
  }
  return menuItems.find((m) => m.key === menuItemKey)?.id
}

/**
 * Return the persisted preference row for a menu item, if any.
 */
export function getUserPreferenceForMenuItem(
  preferences: ReadonlyArray<UserPreferenceReportModel>,
  menuItemId: string | undefined
): UserPreferenceReportModel | undefined {
  if (!menuItemId) return undefined
  return preferences.find((p) => p.menu_item_id === menuItemId)
}

export type PersistUserPreferenceResult = { success: boolean; error?: string }

/**
 * Resolve `menu_item_id` from `menuItemKey` and PATCH the same payload as {@link persistUserPreference}.
 */
export async function persistMenuItemPreference(
  menuItems: ReadonlyArray<MenuItemKeyRow>,
  menuItemKey: string,
  reportId: string | undefined,
  data: Record<string, unknown> = {},
  preferredMenuItemId?: string | null
): Promise<PersistUserPreferenceResult> {
  const menuItemId = getMenuItemIdByKey(menuItems, menuItemKey, preferredMenuItemId)
  if (!menuItemId) {
    return { success: false, error: `Menu item not found for key: ${menuItemKey}` }
  }
  return persistUserPreference(menuItemId, reportId, data)
}

/**
 * Resolve `menu_item_id` from `menuItemKey` and delete that preference row.
 */
export async function deleteMenuItemPreference(
  menuItems: ReadonlyArray<MenuItemKeyRow>,
  menuItemKey: string,
  preferredMenuItemId?: string | null
): Promise<PersistUserPreferenceResult> {
  const menuItemId = getMenuItemIdByKey(menuItems, menuItemKey, preferredMenuItemId)
  if (!menuItemId) {
    return { success: false, error: `Menu item not found for key: ${menuItemKey}` }
  }
  return deleteUserPreference(menuItemId)
}
